/**
 * ADR-0235 — trust label chip + "Why?" popover + "This is wrong" form.
 *
 * Rendered only when the site's verdicts are live (per-site flag on, kill
 * switch off) and a verdict matches the agent row. The evidence list is
 * built from closed reason codes through `reasonCopy` (unknown codes fall
 * back to a neutral sentence; codes are never shown raw).
 */

import { useId, useRef, useState } from 'react';

import {
  TRUST_CLAIM_KEY_RE,
  TRUST_DISPUTE_NOTE_MAX,
  TRUST_LABEL_TEXT,
  TRUST_SUSPICIOUS_TEXT,
  isLabelShown,
  reasonCopy,
  trustLabelHeadline,
  type DisputableLabel,
  type ReasonFamily,
} from '@sill/types';
import type { MerchantAgentDisputeRef, MerchantAgentVerdict } from '@sill/types/api';

import { useOptionalHttp } from '../../../api/http-context.js';
import { createTrustDispute } from '../../../api/trust-verdicts.js';
import { Modal } from '../../../components/atoms/Modal.js';
import { Pill, type PillTone } from '../../../components/atoms/Pill.js';
import { Popover } from '../../../components/atoms/Popover.js';
import { useC } from '../../../lib/theme.js';
import { fmtInt, monoFont, sansFont } from '../format.js';

const TONE: Readonly<Record<MerchantAgentVerdict['label'], PillTone>> = {
  verified: 'mint',
  unverified: 'neutral',
  likely_impersonator: 'coral',
};

const FAMILY_HEADING: Readonly<Record<ReasonFamily, string>> = {
  likely_impersonator: 'Signs of impersonation',
  suspicious: 'Unusual behaviour',
  verified: 'Proof',
  unverified: 'Why nothing stronger',
};

const DISPUTE_LABEL_TEXT: Readonly<Record<DisputableLabel, string>> = {
  verified: 'Verified',
  likely_impersonator: 'Likely impersonator',
  suspicious: 'Suspicious',
};

export interface TrustChipProps {
  readonly verdict: MerchantAgentVerdict;
  /** Display name of the agent row ("GPTBot"). */
  readonly name: string;
  readonly site_id: string;
  /** owner | admin | reviewer, and not a support session. */
  readonly canDispute: boolean;
  /** Window phrase for the footer ("last 30 days"). */
  readonly windowPhrase: string;
  readonly onDisputed?: ((claim_key: string, dispute: MerchantAgentDisputeRef) => void) | undefined;
  /**
   * Dense list rows (visits panel): drop the plain "Unverified" pill when a
   * Suspicious / Impersonated flag already carries the signal. The default
   * state has no word on a list row; the popover is unchanged.
   */
  readonly compact?: boolean | undefined;
}

function shownDisputable(v: MerchantAgentVerdict): DisputableLabel[] {
  return (['likely_impersonator', 'suspicious', 'verified'] as const).filter((l) => isLabelShown(v, l));
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
}

export function TrustChip({ verdict, name: rowName, site_id, canDispute: canDisputeRole, windowPhrase, onDisputed, compact = false }: TrustChipProps): JSX.Element {
  // Security review nit 4: a claim key outside the closed shape cannot be
  // disputed (the API would refuse it) and its UA-derived name is not
  // echoed into the evidence copy.
  const wellFormed = TRUST_CLAIM_KEY_RE.test(verdict.claim_key);
  const name = wellFormed ? rowName : 'this agent';
  const canDispute = canDisputeRole && wellFormed;
  const C = useC();
  const [open, setOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popId = useId();

  const ctx = { name, operator: verdict.operator, signer: verdict.signers[0] ?? null };
  const families: ReasonFamily[] = ['likely_impersonator', 'suspicious', 'verified', 'unverified'];
  const disputable = shownDisputable(verdict);
  const openLabels = new Set(verdict.disputes.filter((d) => d.status === 'open').map((d) => d.disputed_label));
  const canFile = canDispute && disputable.some((l) => !openLabels.has(l));

  return (
    <span
      ref={wrapRef}
      className="relative inline-flex items-center gap-1.5"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      data-testid="trust-chip"
    >
      {compact && verdict.label === 'unverified' && (verdict.suspicious || verdict.impersonated)
        ? null
        : <Pill tone={TONE[verdict.label]}>{TRUST_LABEL_TEXT[verdict.label]}</Pill>}
      {verdict.impersonated ? <Pill tone="coral">Impersonated</Pill> : null}
      {verdict.suspicious ? <Pill tone="amber">{TRUST_SUSPICIOUS_TEXT}</Pill> : null}
      <button
        ref={btnRef}
        type="button"
        aria-expanded={open}
        aria-controls={popId}
        onClick={() => setOpen((v) => !v)}
        data-testid="trust-why"
        style={{
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          fontFamily: monoFont, fontSize: 10, letterSpacing: 0.5, color: C.muted,
          textDecoration: 'underline', textDecorationColor: C.dim, textUnderlineOffset: '3px',
        }}
      >
        Why?
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={btnRef}
        side="bottom"
        id={popId}
        ariaLabel={`Why ${name} is labelled ${TRUST_LABEL_TEXT[verdict.label]}`}
        data-testid="trust-why-popover"
        maxWidth={360}
        style={{ textAlign: 'left', cursor: 'default', whiteSpace: 'normal' }}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: C.muted, marginBottom: 8 }}>
            Why this label
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.45, marginBottom: 10 }}>
            {trustLabelHeadline(verdict.label, name)}
          </div>
          {verdict.impersonated ? (
            <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.coralFg, lineHeight: 1.45, marginBottom: 10 }}>
              {fmtInt(verdict.counts.likely_impersonator)} {verdict.counts.likely_impersonator === 1 ? 'request' : 'requests'} under this name came from someone else.
            </div>
          ) : null}
          {verdict.suspicious ? (
            <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.amberFg, lineHeight: 1.45, marginBottom: 10 }}>
              {fmtInt(verdict.counts.suspicious)} {verdict.counts.suspicious === 1 ? 'request' : 'requests'} behaved unlike the real {name} on your site.
            </div>
          ) : null}
          {families.map((f) => {
            const items = verdict.reasons.filter((r) => r.family === f);
            if (items.length === 0) return null;
            return (
              <div key={f} style={{ marginBottom: 10 }}>
                <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: C.dim, marginBottom: 4 }}>
                  {FAMILY_HEADING[f]}
                </div>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {items.map((r) => (
                    <li key={r.code} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, fontFamily: sansFont, fontSize: 12.5, color: C.body, lineHeight: 1.4 }}>
                      <span>{reasonCopy(r.code, ctx)}</span>
                      <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.count)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          {verdict.suppressed.length > 0 ? (
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.45, marginBottom: 10 }}>
              Sill reviewed a report from your team and removed the {verdict.suppressed.map((l) => `“${DISPUTE_LABEL_TEXT[l]}”`).join(' and ')} label for this agent.
            </div>
          ) : null}
          {openLabels.size > 0 ? (
            <div style={{ fontFamily: sansFont, fontSize: 12, color: C.skyFg, lineHeight: 1.45, marginBottom: 10 }} data-testid="trust-dispute-open">
              Your report is under review.
            </div>
          ) : null}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderTop: `1px solid ${C.borderSoft}`, paddingTop: 8 }}>
            <div style={{ fontFamily: monoFont, fontSize: 9.5, letterSpacing: 0.5, color: C.dim, lineHeight: 1.5 }}>
              {fmtInt(verdict.counts.total)} {verdict.counts.total === 1 ? 'request' : 'requests'}, {windowPhrase}
              <br />
              Rule {verdict.rule_version}{verdict.computed_at.length > 0 ? ` · checked ${fmtWhen(verdict.computed_at)}` : ''}
            </div>
            {canFile ? (
              <button
                type="button"
                onClick={() => { setDisputeOpen(true); setOpen(false); }}
                data-testid="trust-dispute-open-form"
                style={{
                  background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 2, padding: '5px 9px',
                  fontFamily: sansFont, fontSize: 11.5, color: C.text, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                This is wrong
              </button>
            ) : null}
          </div>
        </div>
      </Popover>
      {disputeOpen ? (
        <DisputeForm
          verdict={verdict}
          name={name}
          site_id={site_id}
          labels={disputable.filter((l) => !openLabels.has(l))}
          onClose={() => setDisputeOpen(false)}
          onDisputed={onDisputed}
        />
      ) : null}
    </span>
  );
}

export function DisputeForm({
  verdict, name, site_id, labels, onClose, onDisputed,
}: {
  readonly verdict: MerchantAgentVerdict;
  readonly name: string;
  readonly site_id: string;
  readonly labels: readonly DisputableLabel[];
  readonly onClose: () => void;
  readonly onDisputed?: ((claim_key: string, dispute: MerchantAgentDisputeRef) => void) | undefined;
}): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const [label, setLabel] = useState<DisputableLabel | null>(labels[0] ?? null);
  const [note, setNote] = useState('');
  const [state, setState] = useState<{ kind: 'idle' } | { kind: 'sending' } | { kind: 'done'; message: string } | { kind: 'error'; message: string }>({ kind: 'idle' });

  const submit = async (): Promise<void> => {
    if (http === null || label === null) return;
    setState({ kind: 'sending' });
    const r = await createTrustDispute(http, site_id, { claim_key: verdict.claim_key, disputed_label: label, note });
    if (r.kind === 'ok') {
      onDisputed?.(verdict.claim_key, r.dispute);
      setState({ kind: 'done', message: 'Sent. Sill will review it and remove the label if it is wrong.' });
    } else if (r.kind === 'already_open') {
      setState({ kind: 'done', message: 'A report about this label is already under review.' });
    } else if (r.kind === 'rate_limited') {
      setState({ kind: 'error', message: 'This site has sent the most reports allowed for today. Try again tomorrow.' });
    } else {
      setState({ kind: 'error', message: 'Could not send the report. Try again in a minute.' });
    }
  };

  return (
    <Modal open onClose={onClose} size="sm" title="Report a wrong label" subtitle={`Tell Sill why the label on ${name} is wrong. A person reviews every report.`} closeOnBackdrop={false}>
      <div data-testid="trust-dispute-form" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '18px 24px 22px' }} onClick={(e) => e.stopPropagation()}>
        {labels.length > 1 ? (
          <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
            <legend style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: C.muted, marginBottom: 6 }}>Which label is wrong</legend>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {labels.map((l) => (
                <label key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: sansFont, fontSize: 13, color: C.text, cursor: 'pointer' }}>
                  <input type="radio" name="trust-dispute-label" checked={label === l} onChange={() => setLabel(l)} />
                  {DISPUTE_LABEL_TEXT[l]}
                </label>
              ))}
            </div>
          </fieldset>
        ) : (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body }}>
            Label: <strong style={{ color: C.text, fontWeight: 500 }}>{label === null ? '' : DISPUTE_LABEL_TEXT[label]}</strong>
          </div>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: C.muted }}>Note (optional)</span>
          <textarea
            value={note}
            maxLength={TRUST_DISPUTE_NOTE_MAX}
            onChange={(e) => setNote(e.target.value.slice(0, TRUST_DISPUTE_NOTE_MAX))}
            rows={4}
            placeholder="For example: this is our own monitoring tool, or we allow this agent and it signs its requests."
            data-testid="trust-dispute-note"
            style={{ resize: 'vertical', background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2, padding: 8, fontFamily: sansFont, fontSize: 13, color: C.text }}
          />
          <span style={{ alignSelf: 'flex-end', fontFamily: monoFont, fontSize: 10, color: C.dim }} data-testid="trust-dispute-count">
            {note.length} / {TRUST_DISPUTE_NOTE_MAX}
          </span>
        </label>
        {state.kind === 'done' || state.kind === 'error' ? (
          <div role="status" style={{ fontFamily: sansFont, fontSize: 12.5, color: state.kind === 'error' ? C.coralFg : C.body }}>{state.message}</div>
        ) : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 2, padding: '7px 12px', fontFamily: sansFont, fontSize: 12, color: C.text, cursor: 'pointer' }}
          >
            {state.kind === 'done' ? 'Close' : 'Cancel'}
          </button>
          {state.kind !== 'done' ? (
            <button
              type="button"
              disabled={state.kind === 'sending' || label === null || http === null}
              onClick={() => { void submit(); }}
              data-testid="trust-dispute-submit"
              style={{ background: C.lime, color: C.ink, border: 'none', borderRadius: 2, padding: '7px 12px', fontFamily: sansFont, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: state.kind === 'sending' ? 0.6 : 1 }}
            >
              {state.kind === 'sending' ? 'Sending' : 'Send report'}
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
